import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [toasts, setToasts] = useState([]);

  // Simulated real-time notification generation
  useEffect(() => {
    if (!user) return;

    const generateNotifications = () => {
      const notificationTypes = [
        {
          type: 'goal_update',
          title: 'Goal Progress Updated',
          message: 'Your goal "Increase Customer Satisfaction" was updated to 75%',
          icon: '🎯',
          priority: 'medium'
        },
        {
          type: 'okr_reminder',
          title: 'OKR Check-in Due',
          message: 'Weekly OKR check-in is due for Q4 2024 cycle',
          icon: '🚀',
          priority: 'high'
        },
        {
          type: 'review_reminder',
          title: 'Performance Review Pending',
          message: 'Your self-assessment is due in 3 days',
          icon: '📝',
          priority: 'high'
        },
        {
          type: 'achievement',
          title: 'Achievement Unlocked!',
          message: 'You completed 5 goals this quarter - Goal Achiever badge earned!',
          icon: '🏆',
          priority: 'low'
        },
        {
          type: 'feedback_received',
          title: 'New Feedback Received',
          message: 'You received peer feedback from John Smith',
          icon: '💬',
          priority: 'medium'
        },
        {
          type: 'team_update',
          title: 'Team Performance Update',
          message: 'Your team achieved 85% average goal completion this month',
          icon: '👥',
          priority: 'medium'
        }
      ];

      // Generate initial notifications based on user role
      const initialNotifications = [];
      const initialActivities = [];

      for (let i = 0; i < 8; i++) {
        const randomNotification = notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
        const timestamp = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000); // Random time in last 7 days
        
        const notification = {
          id: `notif_${i}`,
          ...randomNotification,
          timestamp,
          read: Math.random() > 0.6, // 60% chance of being unread
          userId: user.id
        };

        initialNotifications.push(notification);
        
        // Create activity from notification
        initialActivities.push({
          id: `activity_${i}`,
          type: notification.type,
          title: notification.title,
          description: notification.message,
          timestamp: notification.timestamp,
          userId: user.id,
          icon: notification.icon
        });
      }

      setNotifications(initialNotifications.sort((a, b) => b.timestamp - a.timestamp));
      setActivities(initialActivities.sort((a, b) => b.timestamp - a.timestamp));
    };

    generateNotifications();

    // Simulate real-time notifications every 30 seconds
    const interval = setInterval(() => {
      if (Math.random() > 0.7) { // 30% chance every 30 seconds
        addNotification({
          type: 'real_time',
          title: 'Live Update',
          message: 'A colleague just completed their weekly OKR check-in',
          icon: '⚡',
          priority: 'low'
        });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [user]);

  const addNotification = (notification) => {
    const newNotification = {
      id: `notif_${Date.now()}`,
      ...notification,
      timestamp: new Date(),
      read: false,
      userId: user?.id
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50
    
    // Add to activities
    setActivities(prev => [{
      id: `activity_${Date.now()}`,
      type: notification.type,
      title: notification.title,
      description: notification.message,
      timestamp: new Date(),
      userId: user?.id,
      icon: notification.icon
    }, ...prev].slice(0, 100)); // Keep last 100

    // Show toast for high priority notifications
    if (notification.priority === 'high') {
      showToast(notification.title, notification.message, notification.icon);
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  const deleteNotification = (notificationId) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const showToast = (title, message, icon = '📢', duration = 5000) => {
    const toast = {
      id: `toast_${Date.now()}`,
      title,
      message,
      icon,
      timestamp: new Date()
    };

    setToasts(prev => [...prev, toast]);

    // Auto remove toast after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== toast.id));
    }, duration);
  };

  const removeToast = (toastId) => {
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  };

  const getUnreadCount = () => {
    return notifications.filter(notif => !notif.read).length;
  };

  const getRecentActivities = (limit = 10) => {
    return activities.slice(0, limit);
  };

  const getNotificationsByType = (type) => {
    return notifications.filter(notif => notif.type === type);
  };

  const getNotificationsByPriority = (priority) => {
    return notifications.filter(notif => notif.priority === priority);
  };

  // Achievement system
  const checkAchievements = (action, data) => {
    // Example achievement logic
    switch (action) {
      case 'goal_completed':
        if (data.completedGoalsCount >= 5) {
          addNotification({
            type: 'achievement',
            title: 'Goal Achiever!',
            message: 'You completed 5 goals this quarter!',
            icon: '🏆',
            priority: 'low'
          });
        }
        break;
      case 'okr_score_improved':
        if (data.score >= 0.9) {
          addNotification({
            type: 'achievement',
            title: 'OKR Champion!',
            message: 'You achieved 90%+ OKR score!',
            icon: '🚀',
            priority: 'low'
          });
        }
        break;
      case 'streak_milestone':
        if (data.streakDays >= 30) {
          addNotification({
            type: 'achievement',
            title: 'Consistency Master!',
            message: '30-day streak of daily check-ins!',
            icon: '🔥',
            priority: 'low'
          });
        }
        break;
      default:
        break;
    }
  };

  const value = {
    // State
    notifications,
    activities,
    toasts,
    
    // Actions
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    showToast,
    removeToast,
    checkAchievements,
    
    // Getters
    getUnreadCount,
    getRecentActivities,
    getNotificationsByType,
    getNotificationsByPriority
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};